import React, { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { ArrowDropDownIcon, Button } from 'tuui';
import { DropdownButton, DropdownMenu, STYLED_BUTTON_SIZE, StyledButton } from 'taboola-ultimate-ui';
import { useDropdownOptions, useModuleName, useNavigate } from 'hooks';
import useAvailableItems from 'hooks/useAvailableItems';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { isNetworkAccountType } from '../../../account-management';
import { ACCOUNT_CREATOR_PATH } from '../../config/routes/routeTypes';
import styles from '../../../campaigns/modules/campaigns-reports/components/GridToolbar/gridToolbar.module.scss';

const optionsConfigurations = [
    {
        id: 'account',
        value: 'account',
        label: 'Account',
        path: ACCOUNT_CREATOR_PATH,
        permissions: 'LIGHT_PUBLISHER_EDIT',
        visibilityCondition: ({ isNetwork }) => isNetwork,
    },
];

export const CreateAccountButton = () => {
    const moduleName = useModuleName();
    const navigate = useNavigate();
    const selectedAccount = useAccount();
    const isNetwork = isNetworkAccountType(selectedAccount.type);
    const rawOptions = useAvailableItems(optionsConfigurations, {
        selectedAccount,
        moduleName,
        isNetwork,
    });

    const translatedOptions = useDropdownOptions(rawOptions, 'app.gridToolbar.create');

    const handleOnChange = useCallback(
        (option, { closeDropdown } = {}) => {
            const { path } = option;
            navigate(path);

            if (closeDropdown) {
                closeDropdown();
            }
        },
        [navigate]
    );

    if (isEmpty(rawOptions)) {
        return null;
    }

    if (rawOptions.length === 1) {
        const option = rawOptions[0];
        return (
            <div
                data-metrics-event-name={GTM_EVENTS.USABILITY}
                data-metrics-component="Create Account Button"
                data-automation="Create Account Button"
            >
                <Button onClick={() => handleOnChange(option)}>
                    <FormattedMessage id="app.gridToolbar.create.account" defaultMessage="+ NEW ACCOUNT" />
                </Button>
            </div>
        );
    }

    return (
        <div>
            <DropdownButton
                ButtonComponent={StyledButton}
                buttonText={
                    <>
                        <FormattedMessage id="app.gridToolbar.new" defaultMessage="<b>New</b>" />
                        <div className={styles['delimiter']} />
                    </>
                }
                onChange={handleOnChange}
                buttonSize={STYLED_BUTTON_SIZE.MEDIUM}
                options={translatedOptions}
                shouldStretchedByContent
                strechedMenuPosition={DropdownMenu.MENU_POSITION.RIGHT}
                buttonIconAfter={<ArrowDropDownIcon style={{ fontSize: 'x-large' }} />}
            />
        </div>
    );
};
